import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { 
  StudentProfile, 
  StudentProgress, 
  StudentAttendance, 
  CoachNote,
  TierFeatureKey,
  hasAcademyManagementRole,
  optionalE164PhoneSchema,
  formatPhoneNational,
  type MakeupCredit,
  type StudentSiblingSummary,
  type StudentLessonEnrollmentItem,
  type Lesson,
} from '@court-sync/shared';
import { useAuthStore } from '../../../../store/auth';
import { useCurrentAcademy } from '../../../../hooks/useDataStore';
import { useFormatDate } from '../../../../hooks/useFormatDate';
import { Card, CardHeader, CardContent } from '../../../../components/ui/Card';
import { Badge } from '../../../../components/ui/Badge';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Modal,
  Input,
  Field,
  FieldLabel,
  FieldError,
  Textarea,
  Tooltip,
  PhoneField,
} from '@/components/ui';
import { PageHeader } from '@/components/layout/PageHeader';
import { PageFeatureHelp } from '@/components/tour/PageFeatureHelp';
import { usePageTourActions } from '@/components/tour/PageTourHost';
import { useAutoPageTour } from '@/hooks/useAutoPageTour';
import { STUDENTS_MANAGEMENT_TOUR_STEP_IDS } from '@/lib/tour-constants';
import {
  STUDENT_DETAIL_HELP,
  STUDENT_DETAIL_TOUR_KEY,
  scheduleStudentDetailTourStart,
} from '@/lib/tours/studentsManagementTour';
import { DashboardTabNav } from '@/components/dashboard/DashboardTabNav';
import { AddStudentsToLessonModal } from '@/components/students/AddStudentsToLessonModal';
import { NotifyStudentsModal } from '@/components/students/NotifyStudentsModal';
import { ManualGrantCreditModal } from '@/components/makeupCredits/ManualGrantCreditModal';
import { EditManualCreditModal } from '@/components/makeupCredits/EditManualCreditModal';
import makeupcreditService from '@/services/makeupCredit';
import { 
  AttendanceSummary, 
  ProgressChart, 
  CoachNotesPanel,
} from '../../../../components/students';
import { StudentPersonSportRatingsPanel } from '@/components/playerRating/PersonSportRatingsPanel';
import { StudentProgramSkillsPanel } from '@/components/students/StudentProgramSkillsPanel';
import { StudentLessonsPanel } from '@/components/students/StudentLessonsPanel';
import { StudentLessonCalendarModal } from '@/components/students/StudentLessonCalendarModal';
import { BookingDetailDialog } from '@/components/bookings/BookingDetailDialog';
import { CoachSportProgressForm } from '../../../../components/students/CoachSportProgressForm';
import { useAcademySports } from '@/hooks/useAcademySports';
import type { CoachPathwaySkillOption } from '../../../../components/students/CoachNotesPanel';
import { useManagementDashboardFeatureAccess } from '../../../../hooks/useFeatureAccess';
import { academyStudentService } from '../../../../services/academyStudentService';
import {
  invalidateAcademyStudentsQueries,
  patchAcademyStudentInListCache,
} from '@/queries/students';
import { bookingService } from '@/services/booking';
import { lessonEnrollmentService } from '@/services/lessonEnrollment';
import { studentProgressService } from '../../../../services/studentProgressService';
import { studentService } from '../../../../services/student';
import { coachNotesService, COACH_NOTES_PAGE_SIZE } from '../../../../services/coachNotesService';
import type { PaginationState } from '../../../../components/ui/PaginatedTable';
import { coachService } from '../../../../services/coach';
import { programService } from '../../../../services/program';
import { termService } from '@/services/term';
import { 
  User, 
  Calendar, 
  Phone, 
  Heart,
  TrendingUp,
  MessageSquare,
  ChevronLeft,
  GitBranch,
  BookOpen,
  Mail,
  Ticket,
  Users,
  MoreHorizontal,
  Smartphone,
} from 'lucide-react';
import { useDefaultPhoneCountry } from '@/hooks/useDefaultPhoneCountry';
import { toast } from 'react-hot-toast';
import { UserAvatar } from '../../../../components/UserAvatar';
import { DashboardEmptyStateCard, DashboardEmptyStateIcon } from '@/components/dashboard/DashboardEmptyState';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/AlertDialog';

type TabType = 'profile' | 'attendance' | 'progress' | 'pathway' | 'lessons' | 'notes' | 'credits';

function creditStatusVariant(
  status: MakeupCredit['status']
): 'default' | 'secondary' | 'success' | 'warning' | 'destructive' {
  switch (status) {
    case 'available':
      return 'success';
    case 'used':
      return 'secondary';
    case 'expired':
      return 'destructive';
    default:
      return 'default';
  }
}

function formatCreditSource(sourceType: MakeupCredit['sourceType']): string {
  switch (sourceType) {
    case 'absence':
      return 'Absence';
    case 'cancellation':
      return 'Cancellation';
    case 'manual':
      return 'Manual';
    default:
      return sourceType;
  }
}

const calculateAge = (dateOfBirth: Date | undefined | null): number | null => {
  if (!dateOfBirth) return null;
  const today = new Date();
  const birthDate = new Date(dateOfBirth);
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

const editStudentFormSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  dateOfBirth: z.string().optional(),
  gender: z.enum(['male', 'female', 'other']),
  skillLevel: z.enum(['beginner', 'intermediate', 'advanced']),
  medicalNotes: z.string().optional(),
  emergencyContactName: z.string().optional(),
  emergencyContactPhone: optionalE164PhoneSchema,
  emergencyContactRelationship: z.string().optional(),
  inviteEmail: z.string().optional(),
});

type EditStudentFormValues = z.infer<typeof editStudentFormSchema>;

const getSkillLevelVariant = (level: string): 'default' | 'secondary' | 'success' | 'warning' => {
  switch (level) {
    case 'advanced':
      return 'success';
    case 'intermediate':
      return 'warning';
    default:
      return 'secondary';
  }
};

export default function StudentDetailPage() {
  const { studentId } = useParams<{ studentId: string }>();
  const { user } = useAuthStore();
  const { startTourWithCompletionKey } = usePageTourActions();
  const { currentAcademy } = useCurrentAcademy();
  const { sports: academySports, primarySport } = useAcademySports(currentAcademy);
  const { formatDate } = useFormatDate();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<TabType>('profile');
  const [student, setStudent] = useState<StudentProfile | null>(null);
  const [progress, setProgress] = useState<StudentProgress | null>(null);
  const [attendance, setAttendance] = useState<StudentAttendance[]>([]);
  const [notes, setNotes] = useState<CoachNote[]>([]);
  const [notesPage, setNotesPage] = useState(1);
  const [notesPagination, setNotesPagination] = useState<PaginationState | null>(null);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesRefreshNonce, setNotesRefreshNonce] = useState(0);
  const [programIdsForStudent, setProgramIdsForStudent] = useState<string[]>([]);
  const [pathwaySkillOptions, setPathwaySkillOptions] = useState<CoachPathwaySkillOption[]>([]);
  const [pathwayOptionsLoading, setPathwayOptionsLoading] = useState(false);
  const [programNameById, setProgramNameById] = useState<Record<string, string>>({});
  const [pathwayRefreshNonce, setPathwayRefreshNonce] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [primaryLessonLabel, setPrimaryLessonLabel] = useState<string | null>(null);
  const [lessonNameById, setLessonNameById] = useState<Record<string, string>>({});
  const [credits, setCredits] = useState<MakeupCredit[]>([]);
  const [creditsLoading, setCreditsLoading] = useState(false);
  const [showManualGrant, setShowManualGrant] = useState(false);
  const [creditToEdit, setCreditToEdit] = useState<MakeupCredit | null>(null);
  const [creditToDelete, setCreditToDelete] = useState<MakeupCredit | null>(null);
  const [creditActionLoading, setCreditActionLoading] = useState(false);
  const [creditTermNameById, setCreditTermNameById] = useState<Record<string, string>>({});
  const [showAddLessonModal, setShowAddLessonModal] = useState(false);
  const [showLessonCalendarModal, setShowLessonCalendarModal] = useState(false);
  const [detailBookingId, setDetailBookingId] = useState<string | null>(null);
  const [lessonsRefreshKey, setLessonsRefreshKey] = useState(0);
  const [showMoveLessonModal, setShowMoveLessonModal] = useState(false);
  const [moveFromRow, setMoveFromRow] = useState<StudentLessonEnrollmentItem | null>(null);
  const [movePending, setMovePending] = useState<{
    fromRow: StudentLessonEnrollmentItem;
    lesson: Lesson;
    termId?: string;
  } | null>(null);
  const [bookingActionLoading, setBookingActionLoading] = useState(false);
  const [siblings, setSiblings] = useState<StudentSiblingSummary[]>([]);
  const [siblingsLoading, setSiblingsLoading] = useState(false);
  const [showEditStudent, setShowEditStudent] = useState(false);
  const [inviteSending, setInviteSending] = useState(false);
  const [showNotifyModal, setShowNotifyModal] = useState(false);

  const { hasAccess: hasProgramsAccess } = useManagementDashboardFeatureAccess(TierFeatureKey.PROGRAMS);
  const { hasAccess: hasMakeupCreditsAccess } = useManagementDashboardFeatureAccess(TierFeatureKey.MAKEUP_CREDITS);
  const { hasAccess: hasSmsNotificationsAccess } = useManagementDashboardFeatureAccess(
    TierFeatureKey.SMS_NOTIFICATIONS
  );
  const defaultPhoneCountry = useDefaultPhoneCountry({
    country: currentAcademy?.country,
    region: currentAcademy?.region,
  });
  const smsActiveAtAcademy = hasSmsNotificationsAccess && (currentAcademy?.smsEnabled ?? false);

  const notifyDeliveryHint = useMemo(() => {
    if (!student) return undefined;
    if (student.isSelf) {
      return 'This student receives notifications on their own account.';
    }
    if (student.isManual && !student.parentId) {
      return 'Roster-only: email goes to the invite address when on file; in-app and SMS require a linked account.';
    }
    return 'Messages go to this student\'s parent or guardian account.';
  }, [student]);

  // Check access
  useEffect(() => {
    if (!user || !hasAcademyManagementRole(user)) {
      navigate('/dashboard');
      return;
    }
    if (!currentAcademy) {
      navigate('/dashboard');
      return;
    }
  }, [user, currentAcademy, navigate]);

  useLayoutEffect(() => {
    setNotesPage(1);
  }, [studentId]);

  const fetchNotesPage = useCallback(
    async (page: number) => {
      if (!currentAcademy || !studentId) return;
      setNotesLoading(true);
      try {
        const res = await coachNotesService.getStudentNotes(studentId, currentAcademy.id, true, {
          page,
          limit: COACH_NOTES_PAGE_SIZE,
        });
        setNotes(res.data || []);
        setNotesPagination(res.pagination ?? null);
      } catch (err) {
        console.error('Failed to load coach notes:', err);
      } finally {
        setNotesLoading(false);
      }
    },
    [currentAcademy, studentId]
  );

  useEffect(() => {
    if (!studentId || !currentAcademy) return;
    void fetchNotesPage(notesPage);
  }, [studentId, currentAcademy, notesPage, notesRefreshNonce, fetchNotesPage]);

  // Load data
  useEffect(() => {
    if (studentId && currentAcademy) {
      loadStudentData(studentId);
    }
  }, [studentId, currentAcademy]);

  const loadStudentData = async (id: string) => {
    if (!currentAcademy) return;

    try {
      setLoading(true);
      setError(null);

      const [studentResponse, progressResponse, attendanceResponse, lessonsRes] = await Promise.all([
        academyStudentService.getStudentProfile(currentAcademy.id, id),
        studentProgressService.getStudentProgress(id, currentAcademy.id),
        studentService.getStudentAttendance(id, currentAcademy.id),
        academyStudentService.getStudentLessons(currentAcademy.id, id).catch(() => ({ current: [], history: [] })),
      ]);

      if (!studentResponse.data) {
        setError('Student not found');
        setLessonNameById({});
        return;
      }

      setStudent(studentResponse.data);
      setProgress(progressResponse.data);
      setAttendance(attendanceResponse.data || []);
      const lessonNames: Record<string, string> = {};
      for (const item of [...(lessonsRes.current ?? []), ...(lessonsRes.history ?? [])]) {
        lessonNames[item.lessonId] = item.lessonName;
      }
      setLessonNameById(lessonNames);
      const firstCurrent = lessonsRes.current?.[0];
      setPrimaryLessonLabel(firstCurrent?.lessonName ?? null);
    } catch (err: any) {
      console.error('Error loading student data:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to load student data';
      setError(errorMessage);
      setLessonNameById({});
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmMoveToLesson = async () => {
    if (!movePending || !studentId || !currentAcademy) return;
    if (movePending.lesson.id === movePending.fromRow.lessonId) {
      toast.error('Choose a different lesson than the current one');
      return;
    }
    setBookingActionLoading(true);
    try {
      const enroll = await lessonEnrollmentService.enrollStudent(movePending.lesson.id, {
        studentId,
        academyId: currentAcademy.id,
        ...(movePending.termId ? { termId: movePending.termId } : {}),
      });
      if (!enroll.success) {
        toast.error(enroll.error || 'Could not add to the new lesson (they may already be booked)');
        return;
      }
      const del = await bookingService.deleteBooking(movePending.fromRow.bookingId);
      if (!del.success) {
        toast.error(
          (del as { error?: string }).error ||
            'They were added to the new lesson, but the old booking could not be removed. Please fix in bookings.'
        );
      } else {
        toast.success('Moved to the new lesson');
      }
      setMovePending(null);
      await loadStudentData(studentId);
      setLessonsRefreshKey((k) => k + 1);
    } finally {
      setBookingActionLoading(false);
    }
  };

  useEffect(() => {
    if (!studentId || !currentAcademy?.id || !hasProgramsAccess) {
      setProgramIdsForStudent([]);
      return undefined;
    }
    let cancelled = false;
    void coachService
      .getStudentProgramIds(currentAcademy.id, studentId)
      .then((ids) => {
        if (!cancelled) setProgramIdsForStudent(ids);
      })
      .catch((err) => {
        console.error('Failed to load student program ids:', err);
        if (!cancelled) setProgramIdsForStudent([]);
      });
    return () => {
      cancelled = true;
    };
  }, [studentId, currentAcademy?.id, hasProgramsAccess]);

  useEffect(() => {
    const academyId = currentAcademy?.id;
    if (!studentId || !academyId || !hasProgramsAccess || programIdsForStudent.length === 0) {
      setPathwaySkillOptions([]);
      setProgramNameById({});
      setPathwayOptionsLoading(false);
      return undefined;
    }
    let cancelled = false;
    setPathwayOptionsLoading(true);
    void (async () => {
      try {
        const allPrograms = await programService.getPrograms(academyId, true);
        const relevant = allPrograms.filter((p) => programIdsForStudent.includes(p.id));
        const names: Record<string, string> = {};
        const opts: CoachPathwaySkillOption[] = [];
        for (const p of relevant) {
          names[p.id] = p.name;
          const rows = await programService.getStudentProgramSkills(p.id, studentId, academyId);
          const byName = new Map(rows.map((r) => [r.skillName, r]));
          for (const t of p.targetSkills) {
            opts.push({
              programId: p.id,
              programName: p.name,
              skillName: t.name,
              completed: byName.get(t.name)?.completed ?? false,
            });
          }
        }
        if (!cancelled) {
          setPathwaySkillOptions(opts);
          setProgramNameById(names);
        }
      } catch (e) {
        console.error('Failed to load pathway options:', e);
        if (!cancelled) {
          setPathwaySkillOptions([]);
        }
      } finally {
        if (!cancelled) {
          setPathwayOptionsLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [studentId, currentAcademy?.id, hasProgramsAccess, programIdsForStudent, pathwayRefreshNonce]);

  const handleAddNote = async (note: {
    content: string;
    category: CoachNote['category'];
    isPrivate: boolean;
    programId?: string;
    programSkillName?: string;
    rating?: number;
    skillAchieved?: boolean;
  }) => {
    if (!studentId || !user || !currentAcademy) return;

    try {
      await coachNotesService.createNote(studentId, {
        academyId: currentAcademy.id,
        content: note.content,
        category: note.category,
        isPrivate: note.isPrivate,
        ...(note.programId && note.programSkillName != null && note.rating != null
          ? {
              programId: note.programId,
              programSkillName: note.programSkillName,
              rating: note.rating,
              skillAchieved: note.skillAchieved,
            }
          : {}),
      });

      setNotesPage(1);
      setNotesRefreshNonce((n) => n + 1);
      if (note.programId) {
        setPathwayRefreshNonce((n) => n + 1);
      }
      toast.success('Note added successfully');
    } catch (err: any) {
      console.error('Error adding note:', err);
      toast.error(err.response?.data?.error || 'Failed to add note');
    }
  };

  const showProgramSkills =
    hasProgramsAccess && programIdsForStudent.length > 0 && !!studentId && !!currentAcademy?.id;

  const loadCredits = useCallback(async () => {
    if (!currentAcademy?.id || !studentId || !hasMakeupCreditsAccess) return;
    setCreditsLoading(true);
    try {
      const data = await makeupcreditService.getCreditsForStudent(currentAcademy.id, studentId);
      setCredits(data);
    } catch {
      setCredits([]);
    } finally {
      setCreditsLoading(false);
    }
  }, [currentAcademy?.id, studentId, hasMakeupCreditsAccess]);

  const creditsByStatus = useMemo(() => {
    const available: MakeupCredit[] = [];
    const used: MakeupCredit[] = [];
    const expired: MakeupCredit[] = [];
    for (const c of credits) {
      if (c.status === 'used') used.push(c);
      else if (c.status === 'expired') expired.push(c);
      else if (c.status === 'available') available.push(c);
    }
    const byUsedDesc = (a: MakeupCredit, b: MakeupCredit) => {
      const ta = a.usedAt ? new Date(a.usedAt).getTime() : 0;
      const tb = b.usedAt ? new Date(b.usedAt).getTime() : 0;
      return tb - ta;
    };
    const byExpiresAsc = (a: MakeupCredit, b: MakeupCredit) =>
      new Date(a.expiresAt).getTime() - new Date(b.expiresAt).getTime();
    const byExpiresDesc = (a: MakeupCredit, b: MakeupCredit) =>
      new Date(b.expiresAt).getTime() - new Date(a.expiresAt).getTime();
    used.sort(byUsedDesc);
    available.sort(byExpiresAsc);
    expired.sort(byExpiresDesc);
    return { availableCredits: available, usedCredits: used, expiredCredits: expired };
  }, [credits]);

  useEffect(() => {
    if (activeTab === 'credits' && hasMakeupCreditsAccess) {
      void loadCredits();
    }
  }, [activeTab, hasMakeupCreditsAccess, loadCredits]);

  useEffect(() => {
    if (activeTab !== 'credits' || !currentAcademy?.id) {
      return undefined;
    }
    let cancelled = false;
    void termService.getTerms(currentAcademy.id).then((res) => {
      if (cancelled || !res.success || !res.data) return;
      const m: Record<string, string> = {};
      for (const t of res.data) m[t.id] = t.name;
      setCreditTermNameById(m);
    });
    return () => {
      cancelled = true;
    };
  }, [activeTab, currentAcademy?.id]);

  const handleConfirmDeleteManualCredit = useCallback(async () => {
    if (!creditToDelete || !currentAcademy?.id) return;
    setCreditActionLoading(true);
    try {
      await makeupcreditService.deleteManualCredit(creditToDelete.id, currentAcademy.id);
      toast.success('Make-up credit removed');
      setCreditToDelete(null);
      await loadCredits();
    } catch (e: unknown) {
      const ax = e as { response?: { data?: { error?: string } }; message?: string };
      toast.error(ax.response?.data?.error || ax.message || 'Failed to delete credit');
    } finally {
      setCreditActionLoading(false);
    }
  }, [creditToDelete, currentAcademy?.id, loadCredits]);

  useEffect(() => {
    if (!currentAcademy?.id || !studentId) {
      return () => {};
    }
    let cancelled = false;
    setSiblingsLoading(true);
    void academyStudentService
      .getStudentSiblings(currentAcademy.id, studentId)
      .then((list) => {
        if (!cancelled) setSiblings(list);
      })
      .catch(() => {
        if (!cancelled) setSiblings([]);
      })
      .finally(() => {
        if (!cancelled) setSiblingsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currentAcademy?.id, studentId]);

  const tabs = useMemo(
    () =>
      [
        { id: 'profile' as const, label: 'Profile', icon: <User className="w-4 h-4" /> },
        { id: 'attendance' as const, label: 'Attendance', icon: <Calendar className="w-4 h-4" /> },
        { id: 'progress' as const, label: 'Progress', icon: <TrendingUp className="w-4 h-4" /> },
        ...(showProgramSkills
          ? [{ id: 'pathway' as const, label: 'Pathway', icon: <GitBranch className="w-4 h-4" /> }]
          : []),
        { id: 'lessons' as const, label: 'Lessons', icon: <BookOpen className="w-4 h-4" /> },
        ...(hasMakeupCreditsAccess
          ? [{ id: 'credits' as const, label: 'Credits', icon: <Ticket className="w-4 h-4" /> }]
          : []),
        { id: 'notes' as const, label: 'Notes', icon: <MessageSquare className="w-4 h-4" /> },
      ] as const,
    [showProgramSkills, hasMakeupCreditsAccess]
  );

  useEffect(() => {
    if (!showProgramSkills && activeTab === 'pathway') {
      setActiveTab('profile');
    }
  }, [showProgramSkills, activeTab]);

  const editForm = useForm<EditStudentFormValues>({
    resolver: zodResolver(editStudentFormSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      gender: 'male',
      skillLevel: 'beginner',
      medicalNotes: '',
      emergencyContactName: '',
      emergencyContactPhone: '',
      emergencyContactRelationship: '',
      inviteEmail: '',
    },
  });

  useEffect(() => {
    if (!showEditStudent || !student) return;
    editForm.reset({
      firstName: student.firstName,
      lastName: student.lastName,
      dateOfBirth: student.dateOfBirth
        ? new Date(student.dateOfBirth).toISOString().slice(0, 10)
        : '',
      gender: student.gender,
      skillLevel: student.skillLevel,
      medicalNotes: student.medicalNotes ?? '',
      emergencyContactName: student.emergencyContact?.name ?? '',
      emergencyContactPhone: student.emergencyContact?.phone ?? '',
      emergencyContactRelationship: student.emergencyContact?.relationship ?? '',
      inviteEmail: student.inviteEmail ?? '',
    });
  }, [showEditStudent, student, editForm]);

  const onSubmitEditStudent = editForm.handleSubmit(async (vals) => {
    if (!currentAcademy?.id || !studentId || !student) return;
    const canInvite = !student.parentId || student.isManual === true;
    try {
      const hasEc =
        (vals.emergencyContactName?.trim() || '') !== '' ||
        (vals.emergencyContactPhone?.trim() || '') !== '' ||
        (vals.emergencyContactRelationship?.trim() || '') !== '';
      const patch = {
        firstName: vals.firstName.trim(),
        lastName: vals.lastName.trim(),
        gender: vals.gender,
        skillLevel: vals.skillLevel,
        medicalNotes: vals.medicalNotes?.trim() ? vals.medicalNotes.trim() : null,
        dateOfBirth: vals.dateOfBirth?.trim() ? vals.dateOfBirth.trim() : null,
        emergencyContact: hasEc
          ? {
              name: vals.emergencyContactName?.trim() ?? '',
              phone: vals.emergencyContactPhone?.trim() ?? '',
              relationship: vals.emergencyContactRelationship?.trim() ?? '',
            }
          : null,
        ...(canInvite
          ? {
              inviteEmail: vals.inviteEmail?.trim() ? vals.inviteEmail.trim() : null,
            }
          : {}),
      };

      const res = await academyStudentService.updateAcademyStudent(currentAcademy.id, studentId, patch);
      if (res.success && res.data) {
        setStudent(res.data);
        patchAcademyStudentInListCache(res.data);
        invalidateAcademyStudentsQueries(currentAcademy.id);
        setShowEditStudent(false);
        toast.success('Student updated');
      } else {
        toast.error((res as { error?: string }).error || 'Could not save changes');
      }
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Could not save changes');
    }
  });

  const handleSendSignupInvite = useCallback(async () => {
    if (!currentAcademy?.id || !studentId || !student) return;
    const email = student.inviteEmail?.trim();
    if (!email) {
      toast.error('No invite email on file. Edit student details to add one first.');
      return;
    }
    setInviteSending(true);
    try {
      const res = await academyStudentService.sendGuardianInvite(currentAcademy.id, studentId);
      if (res.success) {
        toast.success('Invitation email sent');
      } else {
        toast.error((res as { error?: string }).error || 'Failed to send invitation');
      }
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to send invitation');
    } finally {
      setInviteSending(false);
    }
  }, [currentAcademy?.id, studentId, student]);

  const pathwayEmptyHint =
    !hasProgramsAccess
      ? 'Pathway skill ratings require the Programs feature (Professional or higher).'
      : programIdsForStudent.length === 0
        ? 'This student has no program lesson bookings yet. Pathway skills appear after they are enrolled in a lesson linked to a program.'
        : !pathwayOptionsLoading && pathwaySkillOptions.length === 0
          ? 'No target skills are configured for this student\'s programs. Add target skills in Program management.'
          : null;

  const studentDetailTourContext = useMemo(
    () => ({
      hasPathwayTab: showProgramSkills,
      hasCreditsTab: hasMakeupCreditsAccess,
    }),
    [hasMakeupCreditsAccess, showProgramSkills],
  );
  const studentDetailTourContextRef = useRef(studentDetailTourContext);
  studentDetailTourContextRef.current = studentDetailTourContext;

  const launchStudentDetailTour = useCallback(() => {
    setActiveTab('profile');
    scheduleStudentDetailTourStart(
      startTourWithCompletionKey,
      studentDetailTourContextRef.current,
    );
  }, [startTourWithCompletionKey]);

  const isStudentDetailTourReady = useMemo(
    () => Boolean(student) && !loading && activeTab === 'profile',
    [activeTab, loading, student],
  );

  useAutoPageTour(STUDENT_DETAIL_TOUR_KEY, isStudentDetailTourReady, {
    onStart: launchStudentDetailTour,
  });

  const studentDetailHelpTour = [
    {
      tourKey: STUDENT_DETAIL_TOUR_KEY,
      label: STUDENT_DETAIL_HELP.tourLabel,
      description: STUDENT_DETAIL_HELP.tourDescription,
      onStart: launchStudentDetailTour,
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen">
        <main className="contents">
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        </main>
      </div>
    );
  }

  if (error || !student) {
    return (
      <div className="min-h-screen">
        <main className="contents">
          <div className="flex flex-col items-center justify-center py-20">
            <p className="text-red-600 dark:text-red-400 mb-4">{error || 'Student not found'}</p>
            <Button onClick={() => navigate('/admin/students')}>
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back to Students
            </Button>
          </div>
        </main>
      </div>
    );
  }

  const age = calculateAge(student.dateOfBirth);

  const refreshSportProgress = async () => {
    if (!studentId || !currentAcademy) return;
    const progressRes = await studentProgressService.getStudentProgress(studentId, currentAcademy.id);
    setProgress(progressRes.data ?? null);
  };

  const refreshAfterPathwayAssessment = async () => {
    setPathwayRefreshNonce((n) => n + 1);
    setNotesRefreshNonce((n) => n + 1);
  };

  return (
    <div className="min-h-screen">
      <main className="contents">
        <PageHeader
          title={`${student.firstName} ${student.lastName}`}
          breadcrumbs={[{ label: 'Students', href: '/admin/students' }]}
          subtitle={`Age ${age != null ? age : '—'} · ${student.gender ? student.gender.charAt(0).toUpperCase() + student.gender.slice(1) : '—'} · Joined ${formatDate(student.createdAt)}`}
          help={
            <PageFeatureHelp title={STUDENT_DETAIL_HELP.title} tours={studentDetailHelpTour}>
              <p>{STUDENT_DETAIL_HELP.description}</p>
              {(primaryLessonLabel || !student.isActive) && (
                <div className="flex flex-wrap items-center gap-2">
                  {primaryLessonLabel ? (
                    <Badge variant="secondary">{primaryLessonLabel}</Badge>
                  ) : null}
                  {!student.isActive ? <Badge variant="outline">Inactive</Badge> : null}
                </div>
              )}
            </PageFeatureHelp>
          }
        />

        <div id={STUDENTS_MANAGEMENT_TOUR_STEP_IDS.DETAIL_TABS}>
          <DashboardTabNav<TabType>
            tabs={tabs}
            activeTab={activeTab}
            onChange={setActiveTab}
            ariaLabel="Student sections"
          />
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="grid gap-6 md:grid-cols-2">
              {/* Basic Info */}
              <Card id={STUDENTS_MANAGEMENT_TOUR_STEP_IDS.DETAIL_BASIC_INFO}>
                <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3 space-y-0">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    <User className="w-5 h-5" />
                    Basic Information
                  </h3>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => setShowEditStudent(true)}>
                      Edit student details
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowNotifyModal(true)}
                    >
                      Send notification
                    </Button>
                    <Tooltip
                      content={
                        student.inviteEmail?.trim()
                          ? 'Send an email with a link to the academy register form. The link includes a secure token — guardians can use any email when signing up if they open that link.'
                          : 'Add an invite email on the student first if they should complete online signup.'
                      }
                    >
                      <span className="inline-flex">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={!student.inviteEmail?.trim() || inviteSending}
                          onClick={() => void handleSendSignupInvite()}
                          className="inline-flex items-center gap-2"
                        >
                          <Mail className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
                          {inviteSending ? 'Sending…' : 'Send signup invite'}
                        </Button>
                      </span>
                    </Tooltip>
                  </div>
                </CardHeader>
                <CardContent>
                  <dl className="space-y-4">
                    <div>
                      <dt className="text-sm text-gray-500 dark:text-gray-400">Full Name</dt>
                      <dd className="text-gray-900 dark:text-white">
                        {student.firstName} {student.lastName}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-500 dark:text-gray-400">Date of Birth</dt>
                      <dd className="text-gray-900 dark:text-white">
                        {student.dateOfBirth ? (
                          <>
                            {formatDate(student.dateOfBirth)} ({age != null ? `${age} years old` : 'Age unknown'})
                          </>
                        ) : (
                          <>Not set</>
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-500 dark:text-gray-400">Gender</dt>
                      <dd className="text-gray-900 dark:text-white capitalize">{student.gender}</dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-500 dark:text-gray-400">Skill Level</dt>
                      <dd>
                        <Badge variant={getSkillLevelVariant(student.skillLevel)}>
                          {student.skillLevel}
                        </Badge>
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-500 dark:text-gray-400">Status</dt>
                      <dd>
                        <Badge variant={student.isActive ? 'success' : 'secondary'}>
                          {student.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </dd>
                    </div>
                  </dl>
                </CardContent>
              </Card>

              {/* Emergency contact + booking account */}
              <Card id={STUDENTS_MANAGEMENT_TOUR_STEP_IDS.DETAIL_CONTACT}>
                <CardHeader>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    <Phone className="w-5 h-5" />
                    Emergency & bookings contact
                  </h3>
                </CardHeader>
                <CardContent>
                  {student.emergencyContact ? (
                    <dl className="space-y-4">
                      <div>
                        <dt className="text-sm text-gray-500 dark:text-gray-400">Name</dt>
                        <dd className="text-gray-900 dark:text-white">
                          {student.emergencyContact.name}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm text-gray-500 dark:text-gray-400">Phone</dt>
                        <dd className="text-gray-900 dark:text-white">
                          <a
                            href={`tel:${student.emergencyContact.phone}`}
                            className="text-blue-600 hover:underline"
                          >
                            {student.emergencyContact.phone}
                          </a>
                        </dd>
                      </div>
                      <div>
                        <dt className="text-sm text-gray-500 dark:text-gray-400">Relationship</dt>
                        <dd className="text-gray-900 dark:text-white">
                          {student.emergencyContact.relationship}
                        </dd>
                      </div>
                      {student.emergencyContact.email ? (
                        <div>
                          <dt className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                            <Mail className="w-3.5 h-3.5" aria-hidden />
                            Emergency contact email
                          </dt>
                          <dd className="text-gray-900 dark:text-white">
                            <a
                              href={`mailto:${student.emergencyContact.email}`}
                              className="text-blue-600 hover:underline"
                            >
                              {student.emergencyContact.email}
                            </a>
                          </dd>
                        </div>
                      ) : null}
                    </dl>
                  ) : (
                    <p className="text-gray-500 dark:text-gray-400 text-sm">
                      No emergency contact on file
                    </p>
                  )}

                  <div className="mt-4 pt-4 border-t border-border">
                    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-3">
                      Online booking account
                    </p>
                    <dl className="space-y-3">
                      {student.guardianEmail ? (
                        <div>
                          <dt className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                            <Mail className="w-3.5 h-3.5" aria-hidden />
                            Linked account email
                          </dt>
                          <dd className="text-gray-900 dark:text-white">
                            <a
                              href={`mailto:${student.guardianEmail}`}
                              className="text-blue-600 hover:underline"
                            >
                              {student.guardianEmail}
                            </a>
                          </dd>
                        </div>
                      ) : student.isManual && student.inviteEmail ? (
                        <div>
                          <dt className="text-sm text-gray-500 dark:text-gray-400">Invite email (signup pending)</dt>
                          <dd className="text-gray-900 dark:text-white">
                            <a
                              href={`mailto:${student.inviteEmail}`}
                              className="text-blue-600 hover:underline"
                            >
                              {student.inviteEmail}
                            </a>
                            <span className="ml-1 text-xs text-muted-foreground">(not linked yet)</span>
                          </dd>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          No linked booking account yet. Add an invite email if this person should finish online signup
                          (often a parent for juniors, or their own email for adults).
                        </p>
                      )}
                    </dl>
                  </div>

                  <div className="mt-4 pt-4 border-t border-border">
                    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-3 flex items-center gap-1.5">
                      <Smartphone className="w-3.5 h-3.5" aria-hidden />
                      SMS notifications
                    </p>
                    {!student.parentId && !student.guardianEmail ? (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {student.isManual && student.invitePhone ? (
                          <>
                            Invite phone (signup pending):{' '}
                            <a
                              href={`tel:${student.invitePhone}`}
                              className="text-blue-600 hover:underline"
                            >
                              {formatPhoneNational(student.invitePhone, defaultPhoneCountry) ||
                                student.invitePhone}
                            </a>
                            <span className="ml-1 text-xs text-muted-foreground">
                              — SMS preferences apply after the guardian completes academy signup.
                            </span>
                          </>
                        ) : (
                          <>
                            SMS preferences are stored on the guardian&apos;s academy profile after they
                            create or link a booking account.
                          </>
                        )}
                      </p>
                    ) : (
                      <dl className="space-y-3">
                        <div>
                          <dt className="text-sm text-gray-500 dark:text-gray-400">
                            Booking account mobile
                          </dt>
                          <dd className="text-gray-900 dark:text-white">
                            {student.guardianPhone ? (
                              <a
                                href={`tel:${student.guardianPhone}`}
                                className="text-blue-600 hover:underline"
                              >
                                {formatPhoneNational(student.guardianPhone, defaultPhoneCountry) ||
                                  student.guardianPhone}
                              </a>
                            ) : (
                              <span className="text-sm text-muted-foreground">Not set</span>
                            )}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-sm text-gray-500 dark:text-gray-400">SMS status</dt>
                          <dd className="flex flex-wrap items-center gap-2">
                            {!smsActiveAtAcademy ? (
                              <span className="text-sm text-muted-foreground">
                                SMS is not enabled for this academy.
                              </span>
                            ) : !student.guardianPhone ? (
                              <Badge variant="outline">No mobile number</Badge>
                            ) : student.guardianSmsConsent ? (
                              <Badge variant="success">Opted in</Badge>
                            ) : (
                              <Badge variant="secondary">Not opted in</Badge>
                            )}
                          </dd>
                          {smsActiveAtAcademy && student.guardianPhone ? (
                            <p className="mt-1 text-xs text-muted-foreground">
                              Guardians manage SMS in their profile settings. Reply STOP to opt out.
                            </p>
                          ) : null}
                        </div>
                      </dl>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Siblings (same grid as profile) */}
              <Card
                id={STUDENTS_MANAGEMENT_TOUR_STEP_IDS.DETAIL_SIBLINGS}
                className="md:col-span-2"
              >
                <CardHeader>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    <Users className="w-5 h-5" aria-hidden />
                    Siblings in this academy
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Other students in this academy linked to the same booking account.
                  </p>
                </CardHeader>
                <CardContent>
                  {siblingsLoading ? (
                    <p className="text-sm text-muted-foreground">Loading…</p>
                  ) : siblings.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">No siblings</p>
                  ) : (
                    <div className="overflow-x-auto rounded-xl border border-border bg-card text-card-foreground shadow-sm">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-border">
                            <th className="h-11 whitespace-nowrap px-4 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">Student</th>
                            <th className="h-11 whitespace-nowrap px-4 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">Current lessons</th>
                          </tr>
                        </thead>
                        <tbody>
                          {siblings.map((sib) => (
                            <tr
                              key={sib.id}
                              className="border-b border-border hover:bg-muted/40 last:border-0"
                            >
                              <td className="px-4 py-3 align-middle">
                                <div className="flex items-center gap-2">
                                  <UserAvatar
                                    firstName={sib.firstName}
                                    lastName={sib.lastName}
                                    size="sm"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => navigate(`/admin/students/${sib.id}`)}
                                    className="font-medium text-primary hover:underline text-left"
                                  >
                                    {sib.firstName} {sib.lastName}
                                  </button>
                                </div>
                              </td>
                              <td className="px-4 py-2 text-gray-700 dark:text-gray-300 align-middle">
                                {sib.currentLessonNames.length > 0
                                  ? sib.currentLessonNames.join(', ')
                                  : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Medical Notes */}
              <Card
                id={STUDENTS_MANAGEMENT_TOUR_STEP_IDS.DETAIL_MEDICAL}
                className="md:col-span-2"
              >
                <CardHeader>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    <Heart className="w-5 h-5" />
                    Medical Notes
                  </h3>
                </CardHeader>
                <CardContent>
                  {student.medicalNotes ? (
                    <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
                      <p className="text-gray-700 dark:text-gray-300">{student.medicalNotes}</p>
                    </div>
                  ) : (
                    <p className="text-gray-500 dark:text-gray-400 text-sm">
                      No medical notes on file
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Attendance Tab */}
          {activeTab === 'attendance' && (
            <>
              {attendance.length > 0 ? (
                <AttendanceSummary
                  attendance={attendance}
                  showDetails
                  lessonNameById={lessonNameById}
                  attendanceExport={
                    studentId && currentAcademy
                      ? {
                          academyId: currentAcademy.id,
                          studentId,
                          studentFirstName: student?.firstName,
                          studentLastName: student?.lastName,
                        }
                      : undefined
                  }
                />
              ) : (
                <DashboardEmptyStateCard>
                  <DashboardEmptyStateIcon icon={Calendar} />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    No attendance records yet
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400 mb-4">
                    Attendance appears after this student is enrolled in lessons and sessions are marked.
                  </p>
                </DashboardEmptyStateCard>
              )}
            </>
          )}

          {/* Progress Tab */}
          {activeTab === 'progress' && studentId && currentAcademy && (
            <div className="space-y-6">
              <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                <p>
                  <span className="font-medium text-gray-900 dark:text-white">Sport activity</span> (skill bars
                  below after save) is separate from program pathway ratings in notes. Save the form to record
                  sport-specific skills.
                </p>
                <p>
                  Students and guardians do not edit this here. Coaches can also record from the coach student
                  Progress tab.
                </p>
              </div>
              <CoachSportProgressForm
                studentId={studentId}
                academyId={currentAcademy.id}
                academySports={academySports}
                progress={progress}
                onSaved={refreshSportProgress}
              />
              {progress ? (
                <ProgressChart
                  progress={progress}
                  showAchievements
                  sportId={primarySport?.sportId ?? 'tennis'}
                />
              ) : (
                <DashboardEmptyStateCard>
                  <DashboardEmptyStateIcon icon={TrendingUp} />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    No sport progress saved yet
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400 mb-4">
                    Use the form above to record skill levels and achievements. Charts appear here after you save.
                  </p>
                </DashboardEmptyStateCard>
              )}
            </div>
          )}

          {/* Pathway Tab */}
          {activeTab === 'pathway' && showProgramSkills && studentId && currentAcademy && (
            <div className="space-y-4">
              <StudentProgramSkillsPanel
                academyId={currentAcademy.id}
                studentId={studentId}
                programIds={programIdsForStudent}
                refreshKey={pathwayRefreshNonce}
                onPathwayAssessmentRecorded={refreshAfterPathwayAssessment}
                academyName={currentAcademy.name}
                studentFirstName={student.firstName}
                studentLastName={student.lastName}
                academyLogoUrl={currentAcademy.logoUrl}
                academyPrimaryColor={currentAcademy.primaryColor}
              />
            </div>
          )}

          {/* Lessons Tab */}
          {activeTab === 'lessons' && studentId && currentAcademy && (
            <StudentLessonsPanel
              academyId={currentAcademy.id}
              studentId={studentId}
              refreshKey={lessonsRefreshKey}
              onViewBookingDetails={(bookingId) => setDetailBookingId(bookingId)}
              onRequestMoveToAnother={(row) => {
                setMoveFromRow(row);
                setShowMoveLessonModal(true);
              }}
              enrolledSectionActions={
                <div className="flex flex-col items-stretch gap-2 sm:items-end">
                  <div className="flex flex-wrap gap-2 sm:justify-end">
                    <Button type="button" size="sm" onClick={() => setShowAddLessonModal(true)}>
                      Add to lesson
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setShowLessonCalendarModal(true)}
                    >
                      View in calendar
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground text-left sm:max-w-xs sm:text-right">
                    Use the row <span className="font-medium text-foreground">actions</span> to manage
                    bookings or move enrollments; you will confirm before changes apply.
                  </p>
                </div>
              }
            />
          )}

          {activeTab === 'credits' && studentId && currentAcademy && hasMakeupCreditsAccess && (
            <div className="space-y-6">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="space-y-1">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Make-up credits</h3>
                  {!creditsLoading && credits.length > 0 ? (
                    <p className="text-sm text-muted-foreground">
                      <span className="font-medium text-foreground">
                        {creditsByStatus.availableCredits.length}
                      </span>{' '}
                      available
                      <span className="mx-1.5 text-border">·</span>
                      {credits.length} total
                      {creditsByStatus.usedCredits.length > 0 ? (
                        <>
                          <span className="mx-1.5 text-border">·</span>
                          {creditsByStatus.usedCredits.length} used
                        </>
                      ) : null}
                      {creditsByStatus.expiredCredits.length > 0 ? (
                        <>
                          <span className="mx-1.5 text-border">·</span>
                          {creditsByStatus.expiredCredits.length} expired
                        </>
                      ) : null}
                    </p>
                  ) : null}
                </div>
                {(creditsLoading || credits.length > 0) && (
                  <Button type="button" onClick={() => setShowManualGrant(true)}>
                    Grant credit manually
                  </Button>
                )}
              </div>
              {creditsLoading ? (
                <p className="text-sm text-muted-foreground">Loading…</p>
              ) : credits.length === 0 ? (
                <DashboardEmptyStateCard>
                  <DashboardEmptyStateIcon icon={Ticket} />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    No make-up credits yet
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400 mb-4">
                    Credits from cancellations or manual grants will appear here once this student has them.
                  </p>
                  <Button type="button" onClick={() => setShowManualGrant(true)}>
                    Grant credit manually
                  </Button>
                </DashboardEmptyStateCard>
              ) : (
                <div className="space-y-8">
                  <Card>
                    <CardHeader className="pb-2">
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold">Available</h3>
                        <Badge variant="success">{creditsByStatus.availableCredits.length}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">Credits the student can still redeem.</p>
                    </CardHeader>
                    <CardContent>
                      {creditsByStatus.availableCredits.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-6 text-center">No available credits.</p>
                      ) : (
                        <div className="overflow-x-auto rounded-md border border-border bg-card">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-border bg-muted/30 text-left">
                                <th className="p-3 font-medium">Source</th>
                                <th className="p-3 font-medium">Scope</th>
                                <th className="p-3 font-medium">Term</th>
                                <th className="p-3 font-medium">Expires</th>
                                <th className="p-3 font-medium">Status</th>
                                <th className="p-3 font-medium w-[1%] text-right">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {creditsByStatus.availableCredits.map((c) => (
                                <tr key={c.id} className="border-b border-border/80">
                                  <td className="p-3 font-medium">{formatCreditSource(c.sourceType)}</td>
                                  <td className="p-3 capitalize text-muted-foreground">{c.scope ?? 'session'}</td>
                                  <td className="p-3 text-muted-foreground">
                                    {c.termId ? creditTermNameById[c.termId] ?? '—' : '—'}
                                  </td>
                                  <td className="p-3 text-muted-foreground">{formatDate(c.expiresAt)}</td>
                                  <td className="p-3">
                                    <Badge variant={creditStatusVariant(c.status)}>{c.status}</Badge>
                                  </td>
                                  <td className="p-3 text-right">
                                    {c.sourceType === 'manual' ? (
                                      <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                          <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            className="h-8 w-8 shrink-0 border-border/80 bg-background shadow-sm hover:bg-muted/60"
                                            aria-label="Credit actions"
                                          >
                                            <MoreHorizontal className="h-4 w-4" />
                                          </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end">
                                          <DropdownMenuItem onClick={() => setCreditToEdit(c)}>
                                            Edit
                                          </DropdownMenuItem>
                                          <DropdownMenuItem
                                            className="text-destructive focus:text-destructive"
                                            onClick={() => setCreditToDelete(c)}
                                          >
                                            Delete
                                          </DropdownMenuItem>
                                        </DropdownMenuContent>
                                      </DropdownMenu>
                                    ) : (
                                      <span className="text-xs text-muted-foreground">—</span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-2">
                      <div className="flex items-center gap-2">
                        <h3 className="text-lg font-semibold">Used</h3>
                        <Badge variant="secondary">{creditsByStatus.usedCredits.length}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Credits already redeemed for a session or term re-enrolment.
                      </p>
                    </CardHeader>
                    <CardContent>
                      {creditsByStatus.usedCredits.length === 0 ? (
                        <p className="text-sm text-muted-foreground py-6 text-center">No used credits yet.</p>
                      ) : (
                        <div className="overflow-x-auto rounded-md border border-border bg-card">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-border bg-muted/30 text-left">
                                <th className="p-3 font-medium">Source</th>
                                <th className="p-3 font-medium">Scope</th>
                                <th className="p-3 font-medium">Term</th>
                                <th className="p-3 font-medium">Used on</th>
                                <th className="p-3 font-medium">Redeemed as</th>
                                <th className="p-3 font-medium">Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {creditsByStatus.usedCredits.map((c) => (
                                <tr key={c.id} className="border-b border-border/80">
                                  <td className="p-3 font-medium">{formatCreditSource(c.sourceType)}</td>
                                  <td className="p-3 capitalize text-muted-foreground">{c.scope ?? 'session'}</td>
                                  <td className="p-3 text-muted-foreground">
                                    {c.termId ? creditTermNameById[c.termId] ?? '—' : '—'}
                                  </td>
                                  <td className="p-3 text-muted-foreground">
                                    {c.usedAt ? formatDate(c.usedAt) : '—'}
                                  </td>
                                  <td className="p-3 text-muted-foreground">
                                    {c.scope === 'term' && !c.usedSessionDate
                                      ? 'Full term re-enrolment'
                                      : c.usedSessionDate
                                        ? formatDate(c.usedSessionDate)
                                        : '—'}
                                  </td>
                                  <td className="p-3">
                                    <Badge variant={creditStatusVariant(c.status)}>{c.status}</Badge>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {creditsByStatus.expiredCredits.length > 0 ? (
                    <Card>
                      <CardHeader className="pb-2">
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-semibold text-muted-foreground">Expired (unused)</h3>
                          <Badge variant="outline">{creditsByStatus.expiredCredits.length}</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Credits that expired before they were used.
                        </p>
                      </CardHeader>
                      <CardContent>
                        <div className="overflow-x-auto rounded-md border border-border bg-card">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b border-border bg-muted/30 text-left">
                                <th className="p-3 font-medium">Source</th>
                                <th className="p-3 font-medium">Scope</th>
                                <th className="p-3 font-medium">Term</th>
                                <th className="p-3 font-medium">Expired on</th>
                                <th className="p-3 font-medium">Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {creditsByStatus.expiredCredits.map((c) => (
                                <tr key={c.id} className="border-b border-border/80">
                                  <td className="p-3 font-medium">{formatCreditSource(c.sourceType)}</td>
                                  <td className="p-3 capitalize text-muted-foreground">{c.scope ?? 'session'}</td>
                                  <td className="p-3 text-muted-foreground">
                                    {c.termId ? creditTermNameById[c.termId] ?? '—' : '—'}
                                  </td>
                                  <td className="p-3 text-muted-foreground">{formatDate(c.expiresAt)}</td>
                                  <td className="p-3">
                                    <Badge variant={creditStatusVariant(c.status)}>{c.status}</Badge>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </CardContent>
                    </Card>
                  ) : null}
                </div>
              )}
            </div>
          )}

          {/* Notes Tab */}
          {activeTab === 'notes' && (
            <CoachNotesPanel
              notes={notes}
              notesPagination={notesPagination}
              onNotesPageChange={setNotesPage}
              notesLoading={notesLoading}
              onAddNote={handleAddNote}
              isCoach={true}
              pathwaySkillOptions={showProgramSkills ? pathwaySkillOptions : []}
              programNameById={programNameById}
              pathwayOptionsLoading={hasProgramsAccess && showProgramSkills && pathwayOptionsLoading}
              pathwayEmptyHint={pathwayEmptyHint}
            />
          )}
        </div>
        {currentAcademy && studentId && (
          <>
            <AddStudentsToLessonModal
              isOpen={showAddLessonModal}
              onClose={() => setShowAddLessonModal(false)}
              academyId={currentAcademy.id}
              studentIds={[studentId]}
              onDone={() => {
                void loadStudentData(studentId);
                setLessonsRefreshKey((k) => k + 1);
                setPrimaryLessonLabel(null);
                void academyStudentService
                  .getStudentLessons(currentAcademy.id, studentId)
                  .then((d) => setPrimaryLessonLabel(d.current[0]?.lessonName ?? null))
                  .catch(() => undefined);
              }}
            />
            <AddStudentsToLessonModal
              isOpen={showMoveLessonModal}
              onClose={() => {
                setShowMoveLessonModal(false);
                setMoveFromRow(null);
              }}
              academyId={currentAcademy.id}
              studentIds={[studentId]}
              onDone={() => undefined}
              excludeLessonId={moveFromRow?.lessonId}
              onLessonPicked={(p) => {
                if (!moveFromRow) return;
                setMovePending({ fromRow: moveFromRow, lesson: p.lesson, termId: p.termId });
                setShowMoveLessonModal(false);
                setMoveFromRow(null);
              }}
            />
            <StudentLessonCalendarModal
              open={showLessonCalendarModal}
              onOpenChange={setShowLessonCalendarModal}
              academyId={currentAcademy.id}
              studentId={studentId}
              studentDisplayName={
                student ? `${student.firstName} ${student.lastName}`.trim() : 'Student'
              }
              makeupLessonEnabled={currentAcademy.makeupLessonEnabled ?? false}
              onEnrollmentChanged={() => {
                if (studentId) void loadStudentData(studentId);
                void loadCredits();
              }}
            />
            <BookingDetailDialog
              bookingId={detailBookingId}
              open={detailBookingId !== null}
              onOpenChange={(open) => {
                if (!open) setDetailBookingId(null);
              }}
              studentDisplayName={
                student ? `${student.firstName} ${student.lastName}`.trim() : undefined
              }
            />
            <AlertDialog
              open={!!movePending}
              onOpenChange={(open) => {
                if (!open) setMovePending(null);
              }}
            >
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Confirm move</AlertDialogTitle>
                  <AlertDialogDescription asChild>
                    <div className="text-sm text-muted-foreground space-y-2">
                      {movePending && student ? (
                        <>
                          <p>
                            Move {student.firstName} {student.lastName} from{' '}
                            <span className="font-medium text-foreground">"{movePending.fromRow.lessonName}"</span> to{' '}
                            <span className="font-medium text-foreground">"{movePending.lesson.name}"</span>?
                          </p>
                          <p>
                            They will be added to the new lesson first, then removed from the current booking.
                          </p>
                        </>
                      ) : null}
                    </div>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel disabled={bookingActionLoading}>Cancel</AlertDialogCancel>
                  <Button
                    type="button"
                    disabled={bookingActionLoading}
                    onClick={() => void handleConfirmMoveToLesson()}
                  >
                    {bookingActionLoading ? 'Working…' : 'Confirm move'}
                  </Button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <Modal isOpen={showEditStudent} onClose={() => setShowEditStudent(false)} title="Edit student details" maxWidth="lg">
              {student ? (
                <form onSubmit={onSubmitEditStudent} className="space-y-4 max-h-[min(70vh,520px)] overflow-y-auto px-1">
                  <Field>
                    <FieldLabel htmlFor="edit-firstName">First name</FieldLabel>
                    <Input id="edit-firstName" {...editForm.register('firstName')} autoComplete="off" />
                    <FieldError>{editForm.formState.errors.firstName?.message}</FieldError>
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="edit-lastName">Last name</FieldLabel>
                    <Input id="edit-lastName" {...editForm.register('lastName')} autoComplete="off" />
                    <FieldError>{editForm.formState.errors.lastName?.message}</FieldError>
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="edit-dob">Date of birth</FieldLabel>
                    <Input id="edit-dob" type="date" {...editForm.register('dateOfBirth')} />
                    <p className="mt-1 text-xs text-muted-foreground">Leave empty if unknown.</p>
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="edit-gender">Gender</FieldLabel>
                    <select
                      id="edit-gender"
                      className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                      {...editForm.register('gender')}
                    >
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="edit-skill">Skill level</FieldLabel>
                    <select
                      id="edit-skill"
                      className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                      {...editForm.register('skillLevel')}
                    >
                      <option value="beginner">Beginner</option>
                      <option value="intermediate">Intermediate</option>
                      <option value="advanced">Advanced</option>
                    </select>
                  </Field>
                  {currentAcademy?.organizationId && studentId ? (
                    <StudentPersonSportRatingsPanel
                      academyId={currentAcademy.id}
                      studentId={studentId}
                      organizationId={currentAcademy.organizationId}
                    />
                  ) : null}
                  <Field>
                    <FieldLabel htmlFor="edit-notes">Medical notes</FieldLabel>
                    <Textarea id="edit-notes" rows={3} {...editForm.register('medicalNotes')} />
                  </Field>
                  <fieldset className="space-y-3 rounded-lg border border-border p-3">
                    <legend className="text-sm font-medium px-1">Emergency contact</legend>
                    <Field>
                      <FieldLabel htmlFor="edit-ec-name">Name</FieldLabel>
                      <Input id="edit-ec-name" {...editForm.register('emergencyContactName')} />
                    </Field>
                    <PhoneField
                      control={editForm.control}
                      name="emergencyContactPhone"
                      label="Phone"
                      id="edit-ec-phone"
                      purpose="emergency_contact"
                      defaultCountrySource={{
                        country: currentAcademy?.country,
                        region: currentAcademy?.region,
                      }}
                      placeholder="Phone number"
                    />
                    <Field>
                      <FieldLabel htmlFor="edit-ec-rel">Relationship</FieldLabel>
                      <Input id="edit-ec-rel" {...editForm.register('emergencyContactRelationship')} />
                    </Field>
                  </fieldset>
                  {(!student.parentId || student.isManual === true) && (
                    <fieldset className="space-y-3 rounded-lg border border-dashed border-amber-200 dark:border-amber-800 p-3">
                      <legend className="text-sm font-medium px-1">
                        Invite email — for online signup only. Someone must register for this academy using the invite email
                        exactly (typically a parent for a junior, or the student for adults).
                      </legend>
                      <Field>
                        <FieldLabel htmlFor="edit-invite-email">Invite email</FieldLabel>
                        <Input id="edit-invite-email" type="email" {...editForm.register('inviteEmail')} autoComplete="off" />
                      </Field>
                    </fieldset>
                  )}
                  <div className="flex justify-end gap-2 pt-2">
                    <Button type="button" variant="outline" onClick={() => setShowEditStudent(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={editForm.formState.isSubmitting}>
                      {editForm.formState.isSubmitting ? 'Saving…' : 'Save changes'}
                    </Button>
                  </div>
                </form>
              ) : null}
            </Modal>
            <ManualGrantCreditModal
              isOpen={showManualGrant}
              onClose={() => setShowManualGrant(false)}
              academyId={currentAcademy.id}
              studentId={studentId}
              onGranted={() => void loadCredits()}
            />
            <EditManualCreditModal
              isOpen={!!creditToEdit}
              onClose={() => setCreditToEdit(null)}
              academyId={currentAcademy.id}
              credit={creditToEdit}
              onSaved={() => {
                toast.success('Credit updated');
                void loadCredits();
              }}
            />
            <AlertDialog
              open={!!creditToDelete}
              onOpenChange={(open) => {
                if (!open) setCreditToDelete(null);
              }}
            >
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Remove this make-up credit?</AlertDialogTitle>
                  <AlertDialogDescription>
                    {creditToDelete
                      ? 'This permanently removes the manual credit. It cannot be undone.'
                      : ''}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel disabled={creditActionLoading}>Cancel</AlertDialogCancel>
                  <Button
                    type="button"
                    variant="destructive"
                    disabled={creditActionLoading}
                    onClick={() => void handleConfirmDeleteManualCredit()}
                  >
                    {creditActionLoading ? 'Working…' : 'Remove credit'}
                  </Button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            {studentId && (
              <NotifyStudentsModal
                isOpen={showNotifyModal}
                onClose={() => setShowNotifyModal(false)}
                selectedCount={1}
                smsAvailable={smsActiveAtAcademy}
                deliveryHint={notifyDeliveryHint}
                onSubmit={async ({ subject, message, inApp, email, sms }) => {
                  if (!currentAcademy?.id || !studentId) return;
                  const res = await academyStudentService.notifyStudents(currentAcademy.id, {
                    studentIds: [studentId],
                    subject,
                    message,
                    channels: { inApp, email, sms },
                  });
                  if (res.success && res.data) {
                    toast.success(
                      `Sent: ${res.data.sentInApp} in-app, ${res.data.sentEmail} email, ${res.data.sentSms} SMS`
                    );
                  } else {
                    toast.error((res as { error?: string }).error || 'Failed to send notification');
                  }
                }}
              />
            )}
          </>
        )}
      </main>
    </div>
  );
}
